/**
 * WithdrawDialog -- Withdraw earned gold (USD) to a linked crypto wallet.
 *
 * Sections:
 *   Balance overview   - withdrawable, pending, total withdrawn
 *   Withdraw action    - wallet status, withdraw button, exit fee info
 *   History            - past withdrawals with cancel for pending ones
 */

import Phaser from 'phaser';
import { Colors, CSSColors, createDialogChrome, closeDialog, Fonts, FontSize, Radius } from './GameUITheme';
import { getApiBaseUrl } from '../../utils/ApiConfig';
import { isMobile } from '../../utils/ResponsiveHelper';
const API_URL = getApiBaseUrl();
const PAD = 20;
let EXIT_FEE_PCT = 50; // Default; updated from server based on ISD holdings
const BORDER_COLOR = Colors.borderBright;
const CARD_COLOR = Colors.bgCard;

interface WithdrawBalance {
  balance: number;
  pendingWithdrawals: number;
  totalWithdrawn: number;
}

interface Withdrawal {
  id: string;
  amount: number;
  fee: number;
  net: number;
  chain: string;
  status: string;
  createdAt: string;
  completedAt?: string;
  txHash?: string;
  tokenName?: string | null;
  tokenAmount?: number | null;
}

interface WalletInfo {
  address: string;
  chain: string;
}

export class WithdrawDialog extends Phaser.GameObjects.Container {
  private dialogW: number;
  private dialogH: number;

  // Data
  private balanceData: WithdrawBalance = { balance: 0, pendingWithdrawals: 0, totalWithdrawn: 0 };
  private withdrawals: Withdrawal[] = [];
  private wallet: WalletInfo | null = null;
  private balanceByChain: Record<string, { total: number; count: number; hasWallet: boolean }> = {};
  private linkedWallets: Array<{ chain: string; address: string; isPrimary: boolean }> = [];
  private isdHoldings: { isdBalance: number; gisdBalance: number; holdingsUsd: number } | null = null;
  private tokenName: string = 'USDC';
  private isProcessing = false;
  private withdrawAmount: number | null = null;
  private selectedWithdrawChain: string | null = null;
  private visibilityHandler: (() => void) | null = null;

  // UI containers
  private contentContainer: Phaser.GameObjects.Container;
  private statusText: Phaser.GameObjects.Text;

  constructor(scene: Phaser.Scene) {
    const mobile = isMobile(scene);
    const DIALOG_WIDTH = mobile ? 340 : 600;
    const DIALOG_HEIGHT = mobile ? 560 : 530;

    const { width, height } = scene.cameras.main;
    super(scene, width / 2 - DIALOG_WIDTH / 2, height / 2 - DIALOG_HEIGHT / 2);

    this.dialogW = DIALOG_WIDTH;
    this.dialogH = DIALOG_HEIGHT;

    createDialogChrome(scene, this, this.dialogW, this.dialogH, {
      title: 'Withdraw Gold',
      onClose: () => this.close(),
    });

    // Content area
    this.contentContainer = scene.add.container(PAD, 55);
    this.add(this.contentContainer);

    // Status text at bottom
    this.statusText = scene.add.text(this.dialogW / 2, this.dialogH - 15, '', {
      fontSize: FontSize.sm,
      color: '#889999',
      fontFamily: Fonts.body,
      wordWrap: { width: this.dialogW - 40 },
    });
    this.statusText.setOrigin(0.5);
    this.add(this.statusText);

    this.setDepth(2000);
    scene.add.existing(this);

    this.loadData();

    // Refresh data when user returns to tab (e.g. after clicking email confirm link)
    this.visibilityHandler = () => {
      if (!document.hidden && this.scene && this.active) {
        this.loadData();
      }
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  private close(): void {
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
    document.getElementById('withdraw-wallet-form')?.remove();
    document.getElementById('withdraw-amount-form')?.remove();
    closeDialog(this.scene, this);
  }

  // ============ Auth ============

  private authHeaders(): HeadersInit {
    const token = localStorage.getItem('crimsonmandate_token') || '';
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  }

  // ============ Data Loading ============

  private async loadData(): Promise<void> {
    this.statusText.setText('Loading...');
    this.contentContainer.removeAll(true);

    try {
      const [balanceRes, historyRes] = await Promise.all([
        fetch(`${API_URL}/withdraw/balance`, { headers: this.authHeaders() }),
        fetch(`${API_URL}/withdraw/history`, { headers: this.authHeaders() }),
      ]);

      if (balanceRes.ok) {
        const raw = await balanceRes.json();
        this.balanceData = {
          balance: raw.totalGoldUsd || 0,
          pendingWithdrawals: 0,
          totalWithdrawn: 0,
        };
        // Update exit fee from server (dynamic based on ISD holdings)
        if (raw.exitFeePct != null) EXIT_FEE_PCT = raw.exitFeePct;
        this.isdHoldings = raw.isdHoldings || null;

        // Store per-chain breakdown and linked wallets
        this.balanceByChain = raw.balanceByChain || {};
        this.linkedWallets = raw.linkedWallets || [];

        // Pick the wallet for the chain with the highest unclaimed balance
        let bestChain = raw.chain || 'solana';
        let bestChainBal = 0;
        this.tokenName = 'USDC';
        for (const [ch, info] of Object.entries(this.balanceByChain)) {
          const chainInfo = info as { total: number; hasWallet: boolean; tokenName?: string };
          if (chainInfo.total > bestChainBal && chainInfo.hasWallet) {
            bestChainBal = chainInfo.total;
            bestChain = ch;
            if (chainInfo.tokenName) this.tokenName = chainInfo.tokenName;
          }
        }
        const bestWallet = this.linkedWallets.find(w => w.chain === bestChain);
        if (bestWallet) {
          this.wallet = { address: bestWallet.address, chain: bestWallet.chain };
        } else if (raw.walletAddress) {
          this.wallet = { address: raw.walletAddress, chain: raw.chain || 'solana' };
        }
      }

      if (historyRes.ok) {
        const historyData = await historyRes.json();
        const rawList = historyData.withdrawals || [];
        this.withdrawals = rawList.map((w: any) => ({
          id: w.id,
          amount: w.grossAmountUsd || 0,
          fee: w.exitFeeUsd || 0,
          net: w.netAmountUsd || 0,
          chain: w.chain || 'solana',
          status: w.status || 'unknown',
          createdAt: w.requestedAt || '',
          completedAt: w.completedAt || undefined,
          txHash: w.txHash || undefined,
          tokenName: w.tokenName || null,
          tokenAmount: w.grossTokenAmount || null,
        }));
        // Compute pending and total withdrawn from history
        for (const w of this.withdrawals) {
          if (w.status === 'pending' || w.status === 'processing' || w.status === 'pending_email_confirmation') {
            this.balanceData.pendingWithdrawals += w.net;
          } else if (w.status === 'completed') {
            this.balanceData.totalWithdrawn += w.net;
          }
        }
      }

      this.statusText.setText('');
      this.renderContent();
    } catch (error) {
      console.error('[WithdrawDialog] Failed to load data:', error);
      this.statusText.setText('Failed to load withdrawal data');
    }
  }

  // ============ Render ============

  private renderContent(): void {
    // Clean up any HTML overlays from previous render
    document.getElementById('withdraw-wallet-form')?.remove();
    document.getElementById('withdraw-amount-form')?.remove();
    this.contentContainer.removeAll(true);
    const innerW = this.dialogW - PAD * 2;
    let y = 0;

    // ── Balance Section ──
    y = this.renderBalanceSection(innerW, y);
    y += 12;

    // ── Withdraw Action Section ──
    y = this.renderWithdrawSection(innerW, y);
    y += 12;

    // ── History Section ──
    this.renderHistorySection(innerW, y);
  }

  private renderBalanceSection(innerW: number, startY: number): number {
    let y = startY;

    // Section background
    const panelH = 90;
    const panelBg = this.scene.add.graphics();
    panelBg.fillStyle(Colors.bgPanel, 1);
    panelBg.fillRoundedRect(0, y, innerW, panelH, Radius.md);
    panelBg.lineStyle(1, Colors.borderDim, 0.5);
    panelBg.strokeRoundedRect(0, y, innerW, panelH, Radius.md);
    this.contentContainer.add(panelBg);

    // Title
    const title = this.scene.add.text(12, y + 8, 'BALANCE OVERVIEW', {
      fontSize: FontSize.xs,
      color: CSSColors.textMuted,
      fontFamily: Fonts.heading,
    });
    this.contentContainer.add(title);

    // Withdrawable balance (big number, inline)
    const balanceText = this.scene.add.text(12, y + 26, `Withdrawable Balance: $${this.balanceData.balance.toFixed(2)}`, {
      fontSize: FontSize.xl,
      color: CSSColors.green,
      fontFamily: Fonts.heading,
    });
    this.contentContainer.add(balanceText);

    // Pending + Total Withdrawn on same row
    const pendingText = this.scene.add.text(12, y + 50, `Pending: $${this.balanceData.pendingWithdrawals.toFixed(2)}`, {
      fontSize: FontSize.sm,
      color: '#ffaa00',
      fontFamily: Fonts.body,
    });
    this.contentContainer.add(pendingText);

    const totalText = this.scene.add.text(innerW / 2 + 10, y + 50, `Total Withdrawn: $${this.balanceData.totalWithdrawn.toFixed(2)}`, {
      fontSize: FontSize.sm,
      color: '#88cc88',
      fontFamily: Fonts.body,
    });
    this.contentContainer.add(totalText);

    // Per-chain balance breakdown
    const chainEntries = Object.entries(this.balanceByChain).filter(([, v]) => v.total > 0);
    if (chainEntries.length > 0) {
      let chainY = y + 70;
      for (const [chain, info] of chainEntries) {
        const chainName = chain.charAt(0).toUpperCase() + chain.slice(1);
        const walletLinked = info.hasWallet;
        const tn = (info as any).tokenName || 'USDC';
        const isStable = ['USDC', 'USDT', 'GUSDC', 'GUSDT'].includes(tn);
        const balLabel = `${isStable ? '$' : ''}${info.total.toFixed(isStable ? 2 : 0)} ${tn} on ${chainName}`;
        const chainText = this.scene.add.text(12, chainY, balLabel, {
          fontSize: FontSize.sm,
          color: walletLinked ? '#aaccff' : '#ff8866',
          fontFamily: Fonts.body,
        });
        this.contentContainer.add(chainText);
        if (!walletLinked) {
          const linkText = this.scene.add.text(chainText.x + chainText.width + 4, chainY, '(no wallet linked — click to link)', {
            fontSize: FontSize.sm,
            color: '#ff8866',
            fontFamily: Fonts.body,
          }).setInteractive({ useHandCursor: true });
          linkText.on('pointerover', () => linkText.setColor('#ffaa88'));
          linkText.on('pointerout', () => linkText.setColor('#ff8866'));
          linkText.on('pointerdown', () => {
            this.wallet = null;
            this.renderContent();
          });
          this.contentContainer.add(linkText);
        }
        chainY += 18;
      }
      return y + panelH + (chainEntries.length * 18);
    }

    // Mining info (fallback when no chain breakdown)
    const miningNote = this.scene.add.text(12, y + 72, 'Gold earned from mining is converted to USD for withdrawal', {
      fontSize: FontSize.xs,
      color: CSSColors.textDim,
      fontFamily: Fonts.body,
    });
    this.contentContainer.add(miningNote);

    return y + panelH;
  }

  private renderWithdrawSection(innerW: number, startY: number): number {
    let y = startY;

    // Determine eligible chains (balance > 0 AND linked wallet)
    const eligibleChains = Object.entries(this.balanceByChain)
      .filter(([, info]) => info.total > 0 && info.hasWallet)
      .map(([chain]) => chain);

    // Resolve the active wallet to use
    let activeWallet = this.wallet;
    if (eligibleChains.length > 1) {
      // Multi-chain: use selectedWithdrawChain, defaulting to best balance chain
      if (!this.selectedWithdrawChain || !eligibleChains.includes(this.selectedWithdrawChain)) {
        this.selectedWithdrawChain = eligibleChains[0];
      }
      const linked = this.linkedWallets.find(w => w.chain === this.selectedWithdrawChain);
      if (linked) {
        activeWallet = { address: linked.address, chain: linked.chain };
      }
    }

    // Update tokenName for the active chain
    if (activeWallet) {
      const chainInfo = this.balanceByChain[activeWallet.chain] as any;
      if (chainInfo?.tokenName) this.tokenName = chainInfo.tokenName;
    }

    // How tall the panel needs to be (extra row if chain selector shown)
    const chainSelectorH = eligibleChains.length > 1 ? 28 : 0;
    const panelH = 130 + chainSelectorH;
    const panelBg = this.scene.add.graphics();
    panelBg.fillStyle(0x0d1525, 1);
    panelBg.fillRoundedRect(0, y, innerW, panelH, Radius.md);
    panelBg.lineStyle(1, BORDER_COLOR, 0.3);
    panelBg.strokeRoundedRect(0, y, innerW, panelH, Radius.md);
    this.contentContainer.add(panelBg);

    // Title
    const title = this.scene.add.text(12, y + 8, 'WITHDRAW', {
      fontSize: FontSize.xs,
      color: CSSColors.textMuted,
      fontFamily: Fonts.heading,
    });
    this.contentContainer.add(title);

    // Chain selector (only when multiple eligible chains)
    if (eligibleChains.length > 1) {
      let btnX = 12;
      for (const ch of eligibleChains) {
        const isSelected = ch === this.selectedWithdrawChain;
        const chainLabel = ch.charAt(0).toUpperCase() + ch.slice(1);
        const chainBtn = this.scene.add.text(btnX, y + 24, chainLabel, {
          fontSize: FontSize.xs,
          color: isSelected ? '#44aaff' : '#667788',
          fontFamily: Fonts.heading,
          backgroundColor: isSelected ? '#1a3355' : '#0d1b2a',
          padding: { x: 8, y: 3 },
        }).setInteractive({ useHandCursor: true });
        chainBtn.on('pointerover', () => { if (ch !== this.selectedWithdrawChain) chainBtn.setColor('#aaccff'); });
        chainBtn.on('pointerout', () => { if (ch !== this.selectedWithdrawChain) chainBtn.setColor('#667788'); });
        chainBtn.on('pointerdown', () => {
          this.selectedWithdrawChain = ch;
          document.getElementById('withdraw-amount-form')?.remove();
          this.renderContent();
        });
        this.contentContainer.add(chainBtn);
        btnX += chainBtn.width + 6;
      }
      y += chainSelectorH;
    }

    if (!activeWallet) {
      // No wallet linked — inline HTML form overlay
      const promptText = this.scene.add.text(12, y + 28, 'Link a wallet to withdraw:', {
        fontSize: FontSize.sm,
        color: '#aaccff',
        fontFamily: Fonts.body,
      });
      this.contentContainer.add(promptText);

      // Create an HTML overlay div for the wallet form
      const formDiv = document.createElement('div');
      formDiv.id = 'withdraw-wallet-form';
      formDiv.style.cssText = `
        position:absolute; z-index:9999;
        display:flex; flex-direction:column; gap:8px;
        padding:0;
      `;

      // Chain row
      const chainRow = document.createElement('div');
      chainRow.style.cssText = 'display:flex; gap:6px;';
      const chains = ['solana', 'ethereum', 'bsc', 'galachain'];
      let selectedChain = 'solana';
      const chainButtons: HTMLButtonElement[] = [];

      const updateChainStyles = () => {
        chainButtons.forEach(btn => {
          const sel = btn.dataset.chain === selectedChain;
          btn.style.background = sel ? '#1a3355' : '#0d1b2a';
          btn.style.borderColor = sel ? '#44aaff' : '#334466';
          btn.style.color = sel ? '#44aaff' : '#8899bb';
        });
      };

      chains.forEach(chain => {
        const btn = document.createElement('button');
        btn.dataset.chain = chain;
        btn.textContent = chain.charAt(0).toUpperCase() + chain.slice(1);
        btn.style.cssText = 'padding:5px 14px;font-size:12px;border:1px solid #334466;border-radius:4px;cursor:pointer;font-family:sans-serif;outline:none;';
        btn.onclick = () => { selectedChain = chain; updateChainStyles(); };
        chainButtons.push(btn);
        chainRow.appendChild(btn);
      });
      formDiv.appendChild(chainRow);
      updateChainStyles();

      // Address input row
      const inputRow = document.createElement('div');
      inputRow.style.cssText = 'display:flex; gap:6px;';
      const addrInput = document.createElement('input');
      addrInput.type = 'text';
      addrInput.placeholder = 'Paste wallet address here...';
      addrInput.style.cssText = 'flex:1;padding:7px 10px;font-size:13px;background:#0a0e17;color:#e0e0e0;border:1px solid #44aaff;border-radius:4px;outline:none;font-family:monospace;';
      addrInput.onfocus = () => { addrInput.style.borderColor = '#66ccff'; };
      addrInput.onblur = () => { addrInput.style.borderColor = '#44aaff'; };
      inputRow.appendChild(addrInput);

      const linkBtn = document.createElement('button');
      linkBtn.textContent = 'Link';
      linkBtn.style.cssText = 'padding:7px 20px;font-size:13px;background:#226644;color:#44ff66;border:1px solid #44ff66;border-radius:4px;cursor:pointer;font-weight:bold;font-family:sans-serif;';
      linkBtn.onmouseover = () => { linkBtn.style.background = '#338855'; };
      linkBtn.onmouseout = () => { linkBtn.style.background = '#226644'; };
      linkBtn.onclick = async () => {
        const addr = addrInput.value.trim();
        if (!addr || addr.length < 20) {
          this.statusText.setText('Please enter a valid wallet address (20+ chars).');
          return;
        }
        linkBtn.disabled = true;
        linkBtn.textContent = '...';
        try {
          const res = await fetch(`${API_URL}/profile/me/wallet`, {
            method: 'POST',
            headers: this.authHeaders(),
            body: JSON.stringify({ walletAddress: addr, chain: selectedChain }),
          });
          if (res.ok) {
            this.wallet = { address: addr, chain: selectedChain };
            formDiv.remove();
            this.statusText.setText('Wallet linked!');
            this.renderContent();
          } else {
            const err = await res.json();
            this.statusText.setText(err.error?.message || err.error || 'Failed to link wallet');
            linkBtn.disabled = false;
            linkBtn.textContent = 'Link';
          }
        } catch (e) {
          this.statusText.setText('Network error linking wallet');
          linkBtn.disabled = false;
          linkBtn.textContent = 'Link';
        }
      };
      inputRow.appendChild(linkBtn);
      formDiv.appendChild(inputRow);

      // Position the form over the dialog
      const gameCanvas = this.scene.game.canvas;
      const canvasRect = gameCanvas.getBoundingClientRect();
      const scaleX = canvasRect.width / gameCanvas.width;
      const scaleY = canvasRect.height / gameCanvas.height;
      // Dialog is centered; contentContainer offset = dialog position + PAD
      const dialogX = (gameCanvas.width - this.dialogW) / 2;
      const dialogY = (gameCanvas.height - this.dialogH) / 2;
      const formX = (dialogX + PAD + 12) * scaleX + canvasRect.left;
      const formY = (dialogY + PAD + 30 + y + 64) * scaleY + canvasRect.top;
      const formW = (innerW - 24) * scaleX;
      formDiv.style.left = `${formX}px`;
      formDiv.style.top = `${formY}px`;
      formDiv.style.width = `${formW}px`;

      document.body.appendChild(formDiv);

      // Clean up on dialog close
      this.once('destroy', () => { formDiv.remove(); });

      return y + panelH;
    }

    // Wallet info + change button
    const truncated = activeWallet.address.slice(0, 6) + '...' + activeWallet.address.slice(-4);
    const chainLabel = activeWallet.chain.charAt(0).toUpperCase() + activeWallet.chain.slice(1);
    const walletText = this.scene.add.text(12, y + 28, `Linked: ${truncated} (${chainLabel})`, {
      fontSize: FontSize.sm,
      color: '#aaccff',
      fontFamily: Fonts.body,
    });
    this.contentContainer.add(walletText);

    // Change wallet link
    const changeBtn = this.scene.add.text(walletText.x + walletText.width + 10, y + 28, '[Change]', {
      fontSize: FontSize.xs,
      color: '#44aaff',
      fontFamily: Fonts.body,
    }).setInteractive({ useHandCursor: true });
    changeBtn.on('pointerover', () => changeBtn.setColor('#88ccff'));
    changeBtn.on('pointerout', () => changeBtn.setColor('#44aaff'));
    changeBtn.on('pointerdown', () => {
      this.wallet = null;
      this.renderContent();
    });
    this.contentContainer.add(changeBtn);

    // Amount input row using HTML overlay
    const formDiv = document.createElement('div');
    formDiv.id = 'withdraw-amount-form';
    formDiv.style.cssText = `position:absolute; z-index:9999; display:flex; flex-direction:column; gap:4px; padding:0;`;

    // Input + button row
    const inputRow = document.createElement('div');
    inputRow.style.cssText = 'display:flex; gap:6px; align-items:center;';

    // Use per-chain balance when a specific chain is selected, else total balance
    const chainBalance = (this.selectedWithdrawChain && this.balanceByChain[this.selectedWithdrawChain])
      ? this.balanceByChain[this.selectedWithdrawChain].total
      : this.balanceData.balance;

    const amtInput = document.createElement('input');
    amtInput.type = 'number';
    amtInput.step = '0.01';
    amtInput.min = '0';
    amtInput.max = String(chainBalance);
    amtInput.placeholder = `Amount (max $${chainBalance.toFixed(2)})`;
    amtInput.style.cssText = 'width:160px;padding:7px 10px;font-size:13px;background:#0a0e17;color:#e0e0e0;border:1px solid #44aaff;border-radius:4px;outline:none;font-family:monospace;';
    amtInput.onfocus = () => { amtInput.style.borderColor = '#66ccff'; };
    amtInput.onblur = () => { amtInput.style.borderColor = '#44aaff'; };
    inputRow.appendChild(amtInput);

    const withdrawBtn = document.createElement('button');
    const btnChainName = activeWallet.chain === 'solana' ? 'SOL' : activeWallet.chain === 'ethereum' ? 'ETH' : activeWallet.chain === 'bsc' ? 'BSC' : activeWallet.chain.toUpperCase();
    withdrawBtn.textContent = `Withdraw to ${btnChainName}`;
    withdrawBtn.style.cssText = 'padding:7px 16px;font-size:12px;background:#226644;color:#44ff66;border:1px solid #44ff66;border-radius:4px;cursor:pointer;font-weight:bold;font-family:sans-serif;white-space:nowrap;';
    withdrawBtn.onmouseover = () => { withdrawBtn.style.background = '#338855'; };
    withdrawBtn.onmouseout = () => { withdrawBtn.style.background = '#226644'; };
    inputRow.appendChild(withdrawBtn);
    formDiv.appendChild(inputRow);

    // "all" link + fee info row
    const infoRow = document.createElement('div');
    infoRow.style.cssText = 'display:flex; justify-content:space-between; align-items:center; padding:0 2px;';

    const allLink = document.createElement('span');
    allLink.textContent = 'all';
    allLink.style.cssText = 'color:#44aaff;font-size:11px;cursor:pointer;text-decoration:underline;font-family:sans-serif;';
    allLink.onmouseover = () => { allLink.style.color = '#66ccff'; };
    allLink.onmouseout = () => { allLink.style.color = '#44aaff'; };
    allLink.onclick = () => {
      amtInput.value = chainBalance.toFixed(2);
      amtInput.dispatchEvent(new Event('input'));
    };
    infoRow.appendChild(allLink);

    const feeWrap = document.createElement('span');
    feeWrap.style.cssText = 'display:flex;align-items:center;gap:6px;';
    const feeLabel = document.createElement('span');
    feeLabel.style.cssText = 'color:#ff6644;font-size:11px;font-family:sans-serif;';
    feeLabel.textContent = `Exit fee (${EXIT_FEE_PCT}%): 0.00 ${this.tokenName}`;
    feeWrap.appendChild(feeLabel);

    const feeWhyLink = document.createElement('span');
    feeWhyLink.textContent = 'Lower fee?';
    feeWhyLink.style.cssText = 'color:#44aaff;font-size:10px;cursor:pointer;text-decoration:underline;font-family:sans-serif;';
    feeWhyLink.onclick = () => {
      const h = this.isdHoldings;
      const isdBal = h ? Math.floor(h.isdBalance).toLocaleString() : '0';
      const gisdBal = h ? Math.floor(h.gisdBalance).toLocaleString() : '0';
      const holdUsd = h ? h.holdingsUsd.toFixed(2) : '0.00';
      const tipDiv = document.createElement('div');
      tipDiv.style.cssText = 'position:absolute;right:10px;top:-80px;background:#1a2744;border:1px solid #2a3a5c;border-radius:6px;padding:10px;max-width:260px;z-index:999;font-family:sans-serif;font-size:10px;color:#c8d6e5;line-height:1.5;';
      tipDiv.innerHTML =
        '<b style="color:#FFD700;">Withdrawal Fee Discount</b><br>' +
        'Hold <b style="color:#00ff88;">$ISD</b> or <b style="color:#00ff88;">$GISD</b> tokens to reduce your fee:<br>' +
        '• 0 tokens held → 50% fee<br>' +
        '• $20 worth → 45% fee<br>' +
        '• Each +$20 more → -1% fee<br>' +
        '• Minimum fee: 1% (at $900+)<br><br>' +
        '<span style="color:#8b9dc3;">Your holdings: ' + isdBal + ' ISD + ' + gisdBal + ' GISD ($' + holdUsd + ')</span><br>' +
        '<span style="color:#ff6644;">Your fee: ' + EXIT_FEE_PCT + '%</span><br><br>' +
        '<a href="/tokenomics.html#withdrawal-fees" target="_blank" style="color:#44aaff;">Full details in Tokenomics →</a>';
      const close = document.createElement('span');
      close.textContent = '✕';
      close.style.cssText = 'position:absolute;top:4px;right:8px;cursor:pointer;color:#666;font-size:12px;';
      close.onclick = (e) => { e.stopPropagation(); tipDiv.remove(); };
      tipDiv.appendChild(close);
      feeWrap.style.position = 'relative';
      const existing = feeWrap.querySelector('.fee-tip');
      if (existing) { existing.remove(); return; }
      tipDiv.className = 'fee-tip';
      feeWrap.appendChild(tipDiv);
    };
    feeWrap.appendChild(feeWhyLink);
    infoRow.appendChild(feeWrap);
    formDiv.appendChild(infoRow);

    // Net receive row
    const netRow = document.createElement('div');
    netRow.style.cssText = 'display:flex; justify-content:flex-end; padding:0 2px;';
    const netLabel = document.createElement('span');
    netLabel.style.cssText = 'color:#44ff66;font-size:12px;font-weight:bold;font-family:sans-serif;';
    netLabel.textContent = `You receive: 0.00 ${this.tokenName}`;
    netRow.appendChild(netLabel);
    formDiv.appendChild(netRow);

    // Update fee/net on input change
    amtInput.addEventListener('input', () => {
      const val = parseFloat(amtInput.value) || 0;
      const clamped = Math.min(Math.max(val, 0), chainBalance);
      const fee = clamped * (EXIT_FEE_PCT / 100);
      const net = clamped - fee;
      const isStable = ['USDC', 'USDT', 'GUSDC', 'GUSDT'].includes(this.tokenName);
      const prefix = isStable ? '$' : '';
      const decimals = isStable ? 2 : 0;
      feeLabel.textContent = `Exit fee (${EXIT_FEE_PCT}%): -${prefix}${fee.toFixed(decimals)} ${this.tokenName}`;
      netLabel.textContent = `You receive: ${prefix}${net.toFixed(decimals)} ${this.tokenName}`;
    });

    // Withdraw button handler
    withdrawBtn.onclick = async () => {
      const val = parseFloat(amtInput.value);
      if (!val || val <= 0) {
        this.statusText.setText('Enter an amount to withdraw.');
        return;
      }
      if (val > chainBalance) {
        this.statusText.setText(`Amount exceeds your balance of $${chainBalance.toFixed(2)}`);
        return;
      }
      if (this.isProcessing) return;
      this.withdrawAmount = val;
      this.handleWithdraw();
    };

    // Position the form over the dialog
    const gameCanvas = this.scene.game.canvas;
    const canvasRect = gameCanvas.getBoundingClientRect();
    const scaleX = canvasRect.width / gameCanvas.width;
    const scaleY = canvasRect.height / gameCanvas.height;
    const dialogX = (gameCanvas.width - this.dialogW) / 2;
    const dialogY = (gameCanvas.height - this.dialogH) / 2;
    const formX = (dialogX + PAD + 12) * scaleX + canvasRect.left;
    const formY = (dialogY + PAD + 30 + y + 50) * scaleY + canvasRect.top;
    const formW = (innerW - 24) * scaleX;
    formDiv.style.left = `${formX}px`;
    formDiv.style.top = `${formY}px`;
    formDiv.style.width = `${formW}px`;

    document.body.appendChild(formDiv);
    this.once('destroy', () => { formDiv.remove(); });

    return y + panelH;
  }

  private renderHistorySection(innerW: number, startY: number): void {
    let y = startY;

    // Section title
    const title = this.scene.add.text(0, y, 'WITHDRAWAL HISTORY', {
      fontSize: FontSize.xs,
      color: CSSColors.textMuted,
      fontFamily: Fonts.heading,
    });
    this.contentContainer.add(title);
    y += 20;

    if (this.withdrawals.length === 0) {
      const emptyText = this.scene.add.text(0, y, 'No withdrawal history yet.', {
        fontSize: FontSize.sm,
        color: '#667788',
        fontFamily: Fonts.body,
      });
      this.contentContainer.add(emptyText);
      return;
    }

    // Column header
    const header = this.scene.add.text(0, y, 'Amount         Fee          Net          Status       Date', {
      fontSize: FontSize.xs,
      color: '#667799',
      fontFamily: Fonts.body,
    });
    this.contentContainer.add(header);
    y += 16;

    // Scrollable area height
    const availableH = this.dialogH - 55 - 15 - startY - 40;
    const cardHeight = 44;
    const maxCards = Math.floor(availableH / (cardHeight + 3));
    const displayWithdrawals = this.withdrawals.slice(0, maxCards);

    for (const withdrawal of displayWithdrawals) {
      const card = this.createWithdrawalCard(0, y, innerW, cardHeight, withdrawal);
      this.contentContainer.add(card);
      y += cardHeight + 3;
    }

    if (this.withdrawals.length > maxCards) {
      const moreText = this.scene.add.text(innerW / 2, y + 4, `+ ${this.withdrawals.length - maxCards} more`, {
        fontSize: FontSize.xs,
        color: CSSColors.textDim,
        fontFamily: Fonts.body,
      });
      moreText.setOrigin(0.5, 0);
      this.contentContainer.add(moreText);
    }
  }

  private createWithdrawalCard(
    x: number, y: number, w: number, h: number,
    withdrawal: Withdrawal,
  ): Phaser.GameObjects.Container {
    const card = this.scene.add.container(x, y);

    const bg = this.scene.add.rectangle(0, 0, w, h, CARD_COLOR);
    bg.setOrigin(0, 0);
    bg.setStrokeStyle(1, 0x334466);
    card.add(bg);

    // Status badge
    const statusColors: Record<string, string> = {
      pending_email_confirmation: '#ffcc44',
      pending: '#ffaa00',
      processing: '#ff8844',
      completed: '#44ff66',
      failed: '#ff4444',
      cancelled: '#888888',
    };
    const statusColor = statusColors[withdrawal.status] || '#888888';

    const statusLabel = withdrawal.status === 'pending_email_confirmation' ? 'AWAITING EMAIL' : withdrawal.status.toUpperCase();
    const statusBadge = this.scene.add.text(8, 6, statusLabel, {
      fontSize: FontSize.xs,
      color: statusColor,
      fontFamily: Fonts.heading,
    });
    card.add(statusBadge);

    // Amount
    const amount = withdrawal.amount ?? 0;
    const tn = withdrawal.tokenName;
    const isStablecoin = !tn || ['USDC', 'USDT', 'GUSDC', 'GUSDT'].includes(tn);
    let amountLabel: string;
    if (!isStablecoin && withdrawal.tokenAmount != null) {
      const tokenAmtStr = Math.round(withdrawal.tokenAmount).toLocaleString();
      amountLabel = `${tokenAmtStr} ${tn} (~$${amount.toFixed(2)})`;
    } else {
      amountLabel = `$${amount.toFixed(2)}`;
    }
    const amountText = this.scene.add.text(8, 22, amountLabel, {
      fontSize: FontSize.md,
      color: '#ccddff',
      fontFamily: Fonts.heading,
    });
    card.add(amountText);

    // Fee
    const fee = withdrawal.fee ?? (amount * EXIT_FEE_PCT / 100);
    const feeText = this.scene.add.text(120, 22, `-$${fee.toFixed(2)} fee`, {
      fontSize: FontSize.xs,
      color: '#ff6644',
      fontFamily: Fonts.body,
    });
    card.add(feeText);

    // Net
    const net = withdrawal.net ?? (amount - fee);
    const netText = this.scene.add.text(220, 22, `Net: $${net.toFixed(2)}`, {
      fontSize: FontSize.sm,
      color: CSSColors.green,
      fontFamily: Fonts.body,
    });
    card.add(netText);

    // Date
    const dateStr = new Date(withdrawal.createdAt).toLocaleDateString();
    const dateText = this.scene.add.text(8, 6, dateStr, {
      fontSize: FontSize.xs,
      color: '#778899',
      fontFamily: Fonts.body,
    });
    dateText.setOrigin(0, 0);
    dateText.setX(w - 10 - dateText.width);
    card.add(dateText);

    // Chain
    if (withdrawal.chain) {
      const chainText = this.scene.add.text(w - 10, 22, withdrawal.chain, {
        fontSize: FontSize.xs,
        color: '#667788',
        fontFamily: Fonts.body,
      });
      chainText.setOrigin(1, 0);
      card.add(chainText);
    }

    // Cancel button for pending withdrawals (including pending_email_confirmation)
    if (withdrawal.status === 'pending' || withdrawal.status === 'pending_email_confirmation') {
      const cancelBtnW = 60;
      const cancelBtnH = 20;
      const cancelBtn = this.scene.add.container(w - 10 - cancelBtnW, h - 26);

      const cancelBg = this.scene.add.rectangle(0, 0, cancelBtnW, cancelBtnH, 0x440000);
      cancelBg.setOrigin(0, 0);
      cancelBg.setStrokeStyle(1, 0xff4444);
      cancelBtn.add(cancelBg);

      const cancelText = this.scene.add.text(cancelBtnW / 2, cancelBtnH / 2, 'Cancel', {
        fontSize: FontSize.xs,
        color: '#ff4444',
        fontFamily: Fonts.heading,
      });
      cancelText.setOrigin(0.5);
      cancelBtn.add(cancelText);

      cancelBtn.setSize(cancelBtnW, cancelBtnH);
      cancelBtn.setInteractive(new Phaser.Geom.Rectangle(0, 0, cancelBtnW, cancelBtnH), Phaser.Geom.Rectangle.Contains);
      cancelBtn.on('pointerover', () => cancelBg.setFillStyle(0x660000));
      cancelBtn.on('pointerout', () => cancelBg.setFillStyle(0x440000));
      cancelBtn.on('pointerdown', () => this.handleCancel(withdrawal.id));

      card.add(cancelBtn);
    }

    // Tx hash link for completed
    if (withdrawal.status === 'completed' && withdrawal.txHash) {
      const hashTruncated = withdrawal.txHash.slice(0, 8) + '...';
      const txText = this.scene.add.text(w - 80, h - 18, `TX: ${hashTruncated}`, {
        fontSize: FontSize.xs,
        color: CSSColors.cyan,
        fontFamily: Fonts.body,
      });
      card.add(txText);
    }

    return card;
  }

  // ============ Actions ============

  private async handleWithdraw(): Promise<void> {
    const activeChain = this.selectedWithdrawChain || this.wallet?.chain;
    const activeWalletInfo = (this.selectedWithdrawChain
      ? this.linkedWallets.find(w => w.chain === this.selectedWithdrawChain)
      : this.wallet) || this.wallet;
    if (this.isProcessing || !activeWalletInfo) return;
    if (this.balanceData.balance <= 0) {
      this.statusText.setText('No balance available to withdraw.');
      return;
    }

    this.isProcessing = true;
    this.statusText.setText('Submitting withdrawal request...');
    // Remove HTML form overlays before re-rendering
    document.getElementById('withdraw-amount-form')?.remove();
    this.renderContent();

    try {
      const requestId = crypto.randomUUID();
      const bodyObj: Record<string, unknown> = {
        requestId,
        chain: activeChain || activeWalletInfo.chain,
      };
      if (this.withdrawAmount !== null) {
        bodyObj.amount = this.withdrawAmount;
      }
      const res = await fetch(`${API_URL}/withdraw/request`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify(bodyObj),
      });

      const data = await res.json();
      this.isProcessing = false;
      this.withdrawAmount = null;

      if (res.ok && data.success !== false) {
        this.statusText.setText('Check your email to confirm this withdrawal (link expires in 30 min)');
        // Reload data after a short delay
        this.scene.time.delayedCall(2500, () => {
          if (this.scene) {
            this.loadData();
          }
        });
      } else {
        this.statusText.setText(`Withdrawal failed: ${data.error || data.message || 'Unknown error'}`);
        this.renderContent();
      }
    } catch (error) {
      this.isProcessing = false;
      console.error('[WithdrawDialog] Withdrawal failed:', error);
      this.statusText.setText('Withdrawal request failed. Please try again.');
      this.renderContent();
    }
  }

  private async handleCancel(withdrawalId: string): Promise<void> {
    if (this.isProcessing) return;

    this.isProcessing = true;
    this.statusText.setText('Cancelling withdrawal...');

    try {
      const res = await fetch(`${API_URL}/withdraw/cancel`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify({ withdrawalId }),
      });

      const data = await res.json();
      this.isProcessing = false;

      if (res.ok && data.success !== false) {
        this.statusText.setText('Withdrawal cancelled.');
        this.scene.time.delayedCall(500, () => {
          if (this.scene) {
            this.loadData();
          }
        });
      } else {
        this.statusText.setText(`Cancel failed: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      this.isProcessing = false;
      console.error('[WithdrawDialog] Cancel failed:', error);
      this.statusText.setText('Failed to cancel withdrawal.');
    }
  }

}
